// EPN-94 S1 — Employee Setup Readiness contract, served by EPN.Compensation.Api (a temporary composition
// host — docs/employee-setup-readiness-architecture.md). Unlike most EPN enums, these are STRINGS on the
// wire (a documented, deliberate deviation — see the backend contract), so no numeric label maps here.
//
// The frontend only RENDERS this. It never computes readiness: no field in an Employee record is ever
// inspected here to decide Ready/Blocked.

/** SRS "Payroll Readiness Validation & Blocking Rules" §3. */
export type ReadinessStatus = 'Ready' | 'ReadyWithWarnings' | 'Blocked' | 'NeedsReview' | 'NotApplicable';

/** SRS §2. */
export type ReadinessSeverity = 'Blocker' | 'Warning' | 'Informational';

/** `CapabilityUnavailable`: the SRS requires this but EPN cannot capture it yet — still a blocker.
 *  `Unverifiable`: a dependency was unreachable, so it could not be evaluated. */
export type ReadinessItemState = 'Complete' | 'Missing' | 'NotApplicable' | 'CapabilityUnavailable' | 'Unverifiable';

export interface ReadinessItem {
  key: string;
  area: string;
  label: string;
  severity: ReadinessSeverity;
  state: ReadinessItemState;
  message: string;
  destinationStep: string | null;
  destinationSection: string | null;
}

/** Progress is separate from readiness: it counts only items that could be evaluated. */
export interface SetupProgress {
  completedCount: number;
  applicableCount: number;
  unsupportedCount: number;
  unverifiableCount: number;
  percent: number;
}

export interface EmployeeSetupReadiness {
  employeeId: string;
  asOfDate: string;
  rulesVersion: string;
  checkedAtUtc: string;
  status: ReadinessStatus;
  progress: SetupProgress;
  items: ReadinessItem[];
}

export interface EmployeeSetupReadinessBatch {
  results: EmployeeSetupReadiness[];
  notFoundEmployeeIds: string[];
}

export const readinessStatusLabels: Record<ReadinessStatus, string> = {
  Ready: 'Ready',
  ReadyWithWarnings: 'Ready with warnings',
  Blocked: 'Blocked',
  NeedsReview: 'Needs review',
  NotApplicable: 'Not applicable',
};

/** Shown for every `CapabilityUnavailable` item — the SRS blocker has no backing capability in EPN yet. */
export const UNSUPPORTED_BLOCKER_LABEL = 'Not yet supported — blocks live payroll.';
